// src/components/CollaboratorLandingPage/CollaboratorLandingPage.tsx
/* eslint-disable no-console */
import { useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, Card, Spinner, Form, Button, Alert, Table } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';
import outputs from '../../../amplify_outputs.json';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

type Issue = Schema['Issue']['type'];

/** ─────────────────── LAYOUT: full-viewport, no scroll ─────────────────── */
const VIEWPORT_WRAPPER: CSSProperties = {
  height: '100vh',
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CONTAINER_STYLE: CSSProperties = {
  width: '100%',
  paddingLeft: '12px',
  paddingRight: '12px',
  display: 'flex',
  justifyContent: 'center',
};

const CARD_MAX_W = 900;
const CARD_SHELL: CSSProperties = {
  width: '100%',
  maxWidth: CARD_MAX_W,
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-soft)',
  background: '#fff',
  // Keep card inside viewport without creating a scrollbar
  maxHeight: '92vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const CARD_SHELL_NARROW: CSSProperties = {
  ...CARD_SHELL,
  maxWidth: 820,
};

const CARD_BODY_SCROLLER: CSSProperties = {
  // Internal scroller is disabled to avoid any scrollbar on the screen.
  // Instead, we strictly size inner content so it fits.
  overflow: 'hidden',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const HEADER_BLOCK: CSSProperties = {
  textAlign: 'center',
  marginBottom: 8,
  paddingBottom: 8,
  borderBottom: '1px solid rgba(0,0,0,0.08)',
};

const PROGRESS_WRAP: CSSProperties = {
  marginBottom: 10,
};

const QUESTION_BLOCK: CSSProperties = {
  backgroundColor: '#f8f9fa',
  borderRadius: 6,
  padding: 10,
  marginBottom: 8,
};

const FOOT_ACTIONS: CSSProperties = {
  marginTop: 8,
  paddingTop: 8,
  borderTop: '1px solid rgba(0,0,0,0.08)',
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
};

const LIST_TABLE_WRAP: CSSProperties = {
  // Ensure list view fits without scroll. Keep rows compact.
  fontSize: 14,
};

const TEXTAREA_STYLE: CSSProperties = {
  // Keep within viewport. No internal scrollbar shows due to page overflow hidden.
  height: '24vh',
  minHeight: 160,
  resize: 'none',
};

/** Dev-only logger */
const devLog = (...args: any[]) => {
  if ((import.meta as any)?.env?.MODE === 'development') {
    console.log(...args);
  }
};
const devWarn = (...args: any[]) => {
  if ((import.meta as any)?.env?.MODE === 'development') {
    console.warn(...args);
  }
};

/** ─────────────────── Utils ─────────────────── */
function unwrapList<T>(maybe: unknown): T[] {
  if (!maybe) return [];
  if (Array.isArray(maybe)) return maybe as T[];
  if (typeof maybe === 'object' && maybe !== null && 'data' in (maybe as any)) {
    const data = (maybe as any).data;
    return Array.isArray(data) ? (data as T[]) : [];
  }
  return [];
}
function coerceStrings(arr?: (string | null)[]): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}
function norm(email?: string | null) {
  return (email ?? '').trim().toLowerCase();
}
function toEmailLower(email?: string | null) {
  return norm(email);
}

/** ─────────────────── Config (Email API) ─────────────────── */
const SEND_EMAIL_FUNCTION_URL =
  (outputs as any)?.custom?.sendEmailApiUrl ||
  (import.meta as any)?.env?.VITE_SEND_EMAIL_API_URL ||
  (window as any)?.__SEND_EMAIL_API_URL__ ||
  '';

/** Debug snapshot (dev only) */
function logIssueDebug(prefix: string, issue: Issue | null) {
  if (!issue) {
    devLog(`[CollaboratorLandingPage] ${prefix}: issue is null`);
    return;
  }
  const snapshot = {
    id: issue.id,
    initiatorFirstName: issue.initiatorFirstName,
    initiatorLastName: issue.initiatorLastName,
    initiatorEmail: issue.initiatorEmail,
    collaboratorFirstName: issue.collaboratorFirstName,
    collaboratorLastName: issue.collaboratorLastName,
    collaboratorEmail: issue.collaboratorEmail,
    status: issue.status,
    initiatorIssue: coerceStrings(issue.initiatorIssue as (string | null)[] | undefined),
    collaboratorIssue: coerceStrings(issue.collaboratorIssue as (string | null)[] | undefined),
    desiredOutcomeInitiator: coerceStrings((issue as any).desiredOutcomeInitiator as (string | null)[] | undefined),
    desiredOutcomeCollaborator: coerceStrings((issue as any).desiredOutcomeCollaborator as (string | null)[] | undefined),
    whyOutcomeMattersInitiator: coerceStrings((issue as any).whyOutcomeMattersInitiator as (string | null)[] | undefined),
    whyOutcomeMattersCollaborator: coerceStrings((issue as any).whyOutcomeMattersCollaborator as (string | null)[] | undefined),
    collaboratorJoined: (issue as any).collaboratorJoined ?? false,
  };
  devLog(`[CollaboratorLandingPage] ${prefix}:`, snapshot);
}

/** ─────────────────── InitialProposeQuestions enqueue (fire-and-forget) ─────────────────── */
function getInitialProposeQuestionsUrl(): string {
  return (
    (outputs as any)?.custom?.initialProposeQuestionsApiUrl ||
    (outputs as any)?.custom?.initialProposeQuestionsUrl ||
    (import.meta as any)?.env?.VITE_INITIAL_PROPOSE_QUESTIONS_URL ||
    (outputs as any)?.custom?.proposeIssuesApiUrl ||
    (outputs as any)?.custom?.proposeIssuesUrl ||
    ''
  );
}
function enqueueInitialProposeQuestionsFireAndForget(issueId: string) {
  const url = getInitialProposeQuestionsUrl();
  if (!url) {
    devWarn('[Collaborator] InitialProposeQuestions API URL not configured; skipping enqueue.');
    return;
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ issueId }),
    credentials: 'include',
  }).catch(() => {});
}

/** ─────────────────── Email helpers ─────────────────── */
function buildInitiatorAgreeEmailBody(params: {
  initiatorFirstName?: string | null;
  collaboratorFirstName?: string | null;
  agreeUrl: string;
}) {
  const hi = params.initiatorFirstName?.trim()
    ? `Dear ${params.initiatorFirstName.trim()},`
    : 'Dear there,';
  const coll = params.collaboratorFirstName?.trim()
    ? params.collaboratorFirstName.trim()
    : 'your collaborator';

  return [
    hi,
    '',
    `${coll} has finished their step in your Dfuse session.`,
    'Proposed issues are ready for your review and approval.',
    '',
    'Open the Agree step:',
    params.agreeUrl,
    '',
    'Thanks,',
    'Dfuse Team',
  ].join('\n');
}
function sendInitiatorAgreeEmailFireAndForget(sessionId: string, issue: Issue) {
  if (!SEND_EMAIL_FUNCTION_URL) return;
  const recipient = (issue.initiatorEmail ?? '').trim().toLowerCase();
  if (!recipient) return;

  const agreeUrl = `${window.location.origin}/session/${encodeURIComponent(sessionId)}/agree`;
  const subject = 'Proposed issues are ready for your review';
  const body = buildInitiatorAgreeEmailBody({
    initiatorFirstName: issue.initiatorFirstName,
    collaboratorFirstName: issue.collaboratorFirstName,
    agreeUrl,
  });

  try {
    void fetch(SEND_EMAIL_FUNCTION_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ recipient, subject, body }),
    }).catch(() => {});
  } catch {}
}

/** All-four-NO -> Initiator */
function buildInitiatorAllNoEmailBody(params: {
  initiatorFirstName?: string | null;
  collaboratorFirstName?: string | null;
  communicationStyleUrl: string;
  agreeUrl: string;
}) {
  const hi = params.initiatorFirstName?.trim()
    ? `Dear ${params.initiatorFirstName.trim()},`
    : 'Dear there,';
  const coll = params.collaboratorFirstName?.trim()
    ? params.collaboratorFirstName.trim()
    : 'your collaborator';

  return [
    hi,
    '',
    `${coll} reviewed the four proposed issues but didn't see a match.`,
    'To keep momentum, please complete a quick step to help us pinpoint where perspectives diverge:',
    '',
    `Continue here (Communication Style):`,
    params.communicationStyleUrl,
    '',
    'You can also revisit or refine the proposed issues here:',
    params.agreeUrl,
    '',
    'Thanks,',
    'Dfuse Team',
  ].join('\n');
}
export function sendInitiatorAllNoEmailFireAndForget(sessionId: string, issue: Issue) {
  if (!SEND_EMAIL_FUNCTION_URL) return;

  const recipient = (issue.initiatorEmail ?? '').trim().toLowerCase();
  if (!recipient) return;

  const origin = window.location.origin;
  const communicationStyleUrl = `${origin}/session/${encodeURIComponent(sessionId)}/communication-style`;
  const agreeUrl = `${origin}/session/${encodeURIComponent(sessionId)}/agree`;

  const subject = 'We couldn\'t confirm a shared issue—one quick step to align';
  const body = buildInitiatorAllNoEmailEmailBody({
    initiatorFirstName: issue.initiatorFirstName,
    collaboratorFirstName: issue.collaboratorFirstName,
    communicationStyleUrl,
    agreeUrl,
  });

  try {
    void fetch(SEND_EMAIL_FUNCTION_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ recipient, subject, body }),
    }).catch(() => {});
  } catch {}
}
function buildInitiatorAllNoEmailEmailBody(params: {
  initiatorFirstName?: string | null;
  collaboratorFirstName?: string | null;
  communicationStyleUrl: string;
  agreeUrl: string;
}) {
  return buildInitiatorAllNoEmailBody(params);
}

/** ─────────────────── Profile upsert (collaborator) ─────────────────── */
async function ensureProfileForCurrentUser(emailHint?: string | null) {
  try {
    const user = await getCurrentUser();
    const userId = user?.userId ?? null;
    if (!userId) return;

    let attrs: Record<string, string> = {};
    try {
      attrs = (await fetchUserAttributes()) as Record<string, string>;
    } catch {}

    const email = (emailHint ?? attrs.email ?? '').trim();
    let emailLower = toEmailLower(email);
    const firstName = (attrs.given_name ?? attrs.name ?? '').trim() || undefined;
    const lastName = (attrs.family_name ?? '').trim() || undefined;

    let existing: any = null;
    try {
      const bySub = await client.models.Profile.list(
        { filter: { sub: { eq: userId } }, limit: 1, authMode: 'userPool' }
      );
      existing = (bySub as any)?.data?.[0] ?? null;
      if (!emailLower && existing?.emailLower) emailLower = existing.emailLower as string;
    } catch {}

    if (emailLower) {
      try {
        const got = await client.models.Profile.get({ emailLower }, { authMode: 'userPool' });
        const byPk = (got as any)?.data ?? null;
        if (byPk) existing = byPk;
      } catch {}
    }

    if (!existing && !emailLower) {
      devWarn('[CollaboratorLandingPage] ensureProfileForCurrentUser: missing emailLower; cannot create Profile yet.');
      return;
    }

    if (!existing && emailLower) {
      await client.models.Profile.create(
        {
          emailLower,
          email: email || undefined,
          sub: userId || undefined,
          extra: (firstName || lastName) ? { firstName, lastName } : undefined,
        } as any,
        { authMode: 'userPool' }
      );
      return;
    }

    if (existing) {
      const next: any = { emailLower: existing.emailLower };
      let needUpdate = false;

      const wantEmail = email || existing.email || undefined;
      if (existing.email !== wantEmail) {
        next.email = wantEmail;
        needUpdate = true;
      }

      const wantSub = userId || existing.sub || undefined;
      if (existing.sub !== wantSub) {
        next.sub = wantSub;
        needUpdate = true;
      }

      const prevExtra = (existing.extra ?? {}) as Record<string, any>;
      const prevFirst = prevExtra.firstName ?? undefined;
      const prevLast = prevExtra.lastName ?? undefined;
      const wantFirst = firstName ?? prevFirst;
      const wantLast = lastName ?? prevLast;

      if (wantFirst || wantLast) {
        if (wantFirst !== prevFirst || wantLast !== prevLast) {
          next.extra = { ...prevExtra, firstName: wantFirst, lastName: wantLast };
          needUpdate = true;
        }
      }

      if (needUpdate) {
        await client.models.Profile.update(next, { authMode: 'userPool' });
      }
    }
  } catch (e) {
    devWarn('[CollaboratorLandingPage] ensureProfileForCurrentUser failed:', e);
  }
}

/** ─────────────────── Component ─────────────────── */
export default function CollaboratorLandingPage() {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { email: userEmail } = useUserContext();

  useEffect(() => {
    document.body.classList.add('page-gradient--calm');
    // Prevent the document itself from scrolling while this page is mounted.
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('page-gradient--calm');
      document.documentElement.style.overflow = prevOverflow;
    };
  }, []);

  const sessionId = useMemo(() => {
    const v = decodeURIComponent(id ?? '');
    return v.trim().replace(/\/+$/, '');
  }, [id]);
  const isDetail = !!sessionId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail state
  const [issue, setIssue] = useState<Issue | null>(null);
  const [collaboratorIssueInput, setCollaboratorIssueInput] = useState('');
  const [desiredOutcomeInput, setDesiredOutcomeInput] = useState('');
  const [whyOutcomeInput, setWhyOutcomeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Conversational flow state
  const [currentStep, setCurrentStep] = useState(0);

  // List state
  const [invites, setInvites] = useState<Issue[]>([]);

  // Normalize trailing slash once in detail mode
  useEffect(() => {
    if (!isDetail) return;
    if (location.pathname.endsWith('/') && sessionId.length > 0) {
      navigate(`/coll/${sessionId}`, { replace: true });
    }
  }, [isDetail, location.pathname, navigate, sessionId]);

  // Stamp auth intent for downstream
  useEffect(() => {
    if (!isDetail || !sessionId) return;
    try {
      const nextPath = `/coll/${sessionId}`;
      localStorage.setItem('nextAfterAuth', nextPath);
      localStorage.setItem('pendingSessionId', sessionId);
      localStorage.setItem('registerSource', 'collaborator');
      devLog('[CollaboratorLandingPage] stamped auth intent for', nextPath);
    } catch {}
  }, [isDetail, sessionId]);

  // Auto-redirect to login if not signed in on detail page
  useEffect(() => {
    if (!isDetail || !sessionId) return;
    if (userEmail) return;
    const next = `/coll/${sessionId}`;
    const t = setTimeout(() => {
      devLog('[CollaboratorLandingPage] not signed in, redirecting to /login with next=', next);
      navigate('/login', { state: { next } });
    }, 1200);
    return () => clearTimeout(t);
  }, [isDetail, sessionId, userEmail, navigate]);

  // Ensure collaborator Profile exists once signed in
  useEffect(() => {
    if (!userEmail) return;
    void ensureProfileForCurrentUser(userEmail);
  }, [userEmail]);

  /** LIST MODE: invitations for me */
  useEffect(() => {
    if (isDetail) return;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        if (!userEmail) {
          setLoading(false);
          setError('Please log in to view your invitations.');
          return;
        }

        const res = (await client.models.Issue.list(
          {
            filter: { collaboratorEmail: { eq: userEmail } },
            authMode: 'userPool',
          } as any
        )) as unknown;

        let items = unwrapList<Issue>(res);
        items = items.sort((a, b) => {
          const aT = a.updatedAt ?? a.createdAt ?? '';
          const bT = b.updatedAt ?? b.createdAt ?? '';
          return bT.localeCompare(aT);
        });

        // Keep to max 5 to guarantee fit without scroll.
        setInvites(items.slice(0, 5));
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load invitations.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isDetail, userEmail]);

  /** DETAIL MODE: observeQuery instead of onUpdate */
  useEffect(() => {
    if (!isDetail || !sessionId || !userEmail) return;

    setLoading(true);
    setError(null);
    setIssue(null);
    setCompleted(false);

    let first = true;
    console.log('[Collaborator][observeQuery] start', { sessionId, userEmail });

    const sub = client.models.Issue.observeQuery({
      filter: { id: { eq: sessionId } },
      authMode: 'userPool',
    }).subscribe({
      next: ({ items }) => {
        console.log('[Collaborator][observeQuery] raw items:', items);
        console.log(
          '[Collaborator][observeQuery] sanity:',
          Array.isArray(items)
            ? items.map((it, idx) => ({
                idx,
                isNull: it == null,
                hasId: typeof it?.id === 'string',
                keys: it && Object.keys(it),
              }))
            : '(not an array)'
        );

        if (!Array.isArray(items) || items.some(it => it == null || typeof (it as any).id !== 'string')) {
          console.warn('[Collaborator][observeQuery] invalid items shape; skipping frame.', items);
          return;
        }

        const row = (items as any[])[0] ?? null;
        setIssue(row as Issue | null);

        if (first) {
          logIssueDebug('Loaded', row as Issue | null);
          setLoading(false);
          first = false;
        }
      },
      error: (e) => {
        devWarn('[CollaboratorLandingPage] observeQuery error:', e);
        setError('Failed to load invitation.');
        setLoading(false);
      },
    });

    return () => {
      try { sub.unsubscribe(); } catch {}
    };
  }, [isDetail, sessionId, userEmail]);

  const signedInEmail = useMemo(() => norm(userEmail), [userEmail]);
  const expectedEmail = useMemo(() => norm(issue?.collaboratorEmail), [issue]);
  const emailMatches = useMemo(() => !!expectedEmail && expectedEmail === signedInEmail, [expectedEmail, signedInEmail]);

  const initiatorName = useMemo(() => {
    if (!issue) return '';
    const n = `${issue.initiatorFirstName ?? ''} ${issue.initiatorLastName ?? ''}`.trim();
    return n || (issue.initiatorEmail ?? '');
  }, [issue]);

  const priorSubmission = useMemo(() => {
    if (!issue) return false;
    const anyText = coerceStrings(issue.collaboratorIssue as (string | null)[] | undefined).length > 0;
    const desired =
      Array.isArray((issue as any).desiredOutcomeCollaborator) &&
      coerceStrings((issue as any).desiredOutcomeCollaborator as (string | null)[]).length > 0;

    const whyProvided =
      Array.isArray((issue as any).whyOutcomeMattersCollaborator) &&
      coerceStrings((issue as any).whyOutcomeMattersCollaborator as (string | null)[]).length > 0;

    const joined = !!(issue as any).collaboratorJoined;
    return anyText || desired || whyProvided || joined;
  }, [issue]);

  /** Conversational flow steps */
  const conversationalSteps = [
    {
      question: `Hi! ${initiatorName} has invited you to work through an issue together.\n\nWhat's the issue as you see it?`,
      placeholder: 'Share your view of the situation...',
      value: collaboratorIssueInput,
      setter: setCollaboratorIssueInput,
    },
    {
      question: 'What is your desired outcome for this disagreement?',
      placeholder: 'Describe your desired outcome...',
      value: desiredOutcomeInput,
      setter: setDesiredOutcomeInput,
    },
    {
      question: 'Why does solving this issue matter to you?',
      placeholder: 'Explain why this matters...',
      value: whyOutcomeInput,
      setter: setWhyOutcomeInput,
    },
  ] as const;

  const handleNext = () => {
    const step = conversationalSteps[currentStep];
    if (!step.value.trim()) {
      setError('Please provide a response before continuing.');
      return;
    }
    setError(null);
    if (currentStep < conversationalSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const currentStepData = conversationalSteps[currentStep];
  const isLastStep = currentStep === conversationalSteps.length - 1;
  const canProceed = currentStepData.value.trim().length > 0;

  /** Actions */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sessionId || !issue) return;

    if (!emailMatches) {
      setError('You are signed in with a different account than the invited collaborator.');
      return;
    }

    const text = collaboratorIssueInput.trim();
    const desired = desiredOutcomeInput.trim();
    const why = whyOutcomeInput.trim();
    if (!text || !desired || !why) {
      setError('Please enter your issue, your desired outcome, and why that outcome matters.');
      return;
    }

    try {
      setSaving(true);

      const existingIssues = coerceStrings(issue.collaboratorIssue as (string | null)[] | undefined);
      const existingDesired = coerceStrings((issue as any).desiredOutcomeCollaborator as (string | null)[] | undefined);
      const existingWhyCurrent = coerceStrings(
        (issue as any).whyOutcomeMattersCollaborator as (string | null)[] | undefined
      );

      const updateInput: any = {
        id: sessionId,
        collaboratorIssue: [...existingIssues, text],
        desiredOutcomeCollaborator: [...existingDesired, desired],
        whyOutcomeMattersCollaborator: [...existingWhyCurrent, why],
        collaboratorJoined: true,
        status: 'accepted',
        lastActivityAt: new Date().toISOString(),
      };

      await client.models.Issue.update(updateInput, { authMode: 'userPool' });

      enqueueInitialProposeQuestionsFireAndForget(sessionId);
      void sendInitiatorAgreeEmailFireAndForget(sessionId, issue);

      setCompleted(true);
      navigate(`/session/${sessionId}/questions`, { replace: true });
    } catch (e: any) {
      devWarn('[Collaborator] Submit error:', e);
      setError(e?.message ?? 'Failed to save your response.');
    } finally {
      setSaving(false);
    }
  }

  /** ─────────────────── Render helpers ─────────────────── */

  type ShellProps = {
  narrow?: boolean;
  children: ReactNode;
  variant?: 'pillar-blue' | 'pillar-yellow' | 'pillar-green';
};

function Shell({ narrow, children, variant = 'pillar-blue' }: ShellProps) {
  return (
    <div style={VIEWPORT_WRAPPER} className="gradient-calm">
      <Container fluid="md" style={CONTAINER_STYLE}>
        <Card className={`mx-auto text-center ${variant}`} style={narrow ? CARD_SHELL_NARROW : CARD_SHELL}>
          <Card.Body style={CARD_BODY_SCROLLER}>{children}</Card.Body>
        </Card>
      </Container>
    </div>
  );
}

  /** ─────────────────── Render ─────────────────── */

  // LIST MODE (/coll)
  if (!isDetail) {
    return (
      <div style={VIEWPORT_WRAPPER} className="gradient-calm">
        <Container fluid="md" style={CONTAINER_STYLE}>
          <Card className="mx-auto pillar-blue" style={CARD_SHELL}>
            <Card.Header as="h6" className="py-2">Your Invitations</Card.Header>
            <Card.Body style={{ ...CARD_BODY_SCROLLER, gap: 6 }}>
              {loading && (
                <div className="text-center">
                  <Spinner animation="border" role="status" />
                  <div className="mt-2">Loading your invitations…</div>
                </div>
              )}

              {!loading && error && <Alert variant="danger" className="mb-0">{error}</Alert>}

              {!loading && !error && invites.length === 0 && (
                <Alert variant="secondary" className="mb-0">
                  No new invitations at the moment.
                </Alert>
              )}

              {!loading && !error && invites.length > 0 && (
                <div style={LIST_TABLE_WRAP}>
                  <Table hover responsive className="mb-0 text-center align-middle">
                    <thead>
                      <tr>
                        <th>From</th>
                        <th>Created</th>
                        <th style={{ width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((it) => {
                        const from =
                          `${it.initiatorFirstName ?? ''} ${it.initiatorLastName ?? ''}`.trim() ||
                          it.initiatorEmail;
                        return (
                          <tr key={it.id}>
                            <td>{from}</td>
                            <td>{it.createdAt ?? '—'}</td>
                            <td>
                              <Button size="sm" onClick={() => navigate(`/coll/${it.id}`)}>
                                Open
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  // Not signed in yet on detail page
  if (!userEmail) {
    const next = `/coll/${sessionId}`;
    return (
      <Shell narrow>
        <div className="py-3">
          <Spinner animation="border" role="status" />
          <div className="mt-2">Checking your account…</div>
          <div className="mt-3 d-flex justify-content-center gap-2">
            <Button onClick={() => navigate('/login', { state: { next } })}>Log in</Button>
            <Button variant="outline-secondary" onClick={() => navigate('/')}>Home</Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell narrow>
        <div className="py-3">
          <Spinner animation="border" role="status" />
          <div className="mt-2">Loading your invitation…</div>
        </div>
      </Shell>
    );
  }

  if (error || !issue) {
    return (
      <Shell narrow variant="pillar-yellow">
        {error
          ? <Alert variant="danger" className="mb-2">{error}</Alert>
          : <Alert variant="warning" className="mb-2">Invitation not found.</Alert>
        }
        <div className="d-flex justify-content-center gap-2">
          <Button variant="outline-secondary" onClick={() => navigate('/')}>Home</Button>
        </div>
      </Shell>
    );
  }

  // Wrong account
  if (!emailMatches) {
    return (
      <Shell narrow variant="pillar-yellow">
        <Alert variant="warning" className="mb-2 text-start">
          <div className="mb-1"><strong>This invitation is for {issue.collaboratorEmail}.</strong></div>
          <div className="small">You're signed in as <code>{userEmail}</code>. Please switch to the invited account.</div>
        </Alert>
        <div className="d-flex flex-wrap justify-content-center gap-2">
          <Button
            variant="outline-danger"
            onClick={() => navigate('/logout', { state: { next: location.pathname + location.search } })}
          >
            Switch Account
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate('/login', { state: { next: location.pathname + location.search } })}
          >
            Log In
          </Button>
          <Button variant="secondary" onClick={() => navigate('/')}>Home</Button>
        </div>
      </Shell>
    );
  }

  const showThankYou = completed || priorSubmission;

  if (showThankYou) {
    return (
      <Shell narrow variant="pillar-green">
        <h5 className="mb-1">Thanks for joining!</h5>
        <div className="text-muted mb-2">
          Thanks for joining the session with <strong>{initiatorName}</strong>.
        </div>
        <div className="d-flex justify-content-center gap-2">
          <Button onClick={() => navigate(`/session/${sessionId}/questions`)}>Open Agree Step</Button>
          <Button variant="outline-secondary" onClick={() => navigate('/')}>Home</Button>
        </div>
      </Shell>
    );
  }

  // EDIT/COMPLETE FORM - Conversational Flow
  return (
    <div style={VIEWPORT_WRAPPER} className="gradient-calm">
      <Container fluid="md" style={CONTAINER_STYLE}>
        <Card className="mx-auto pillar-blue" style={CARD_SHELL}>
          <Card.Body style={CARD_BODY_SCROLLER}>
            {/* Warm Welcome Header */}
            <div style={HEADER_BLOCK}>
              <h5 className="mb-1">Welcome</h5>
              <p className="text-muted mb-1">
                <strong>{initiatorName}</strong> invited you to collaborate
              </p>
              <p className="small text-muted mb-0">Share your perspective</p>
            </div>

            {/* Progress Indicator */}
            <div style={PROGRESS_WRAP}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Step {currentStep + 1} of {conversationalSteps.length}</small>
                <small className="text-muted">{Math.round(((currentStep + 1) / conversationalSteps.length) * 100)}% complete</small>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${((currentStep + 1) / conversationalSteps.length) * 100}%` }}
                />
              </div>
            </div>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (isLastStep) {
                  void handleSubmit(e);
                } else {
                  handleNext();
                }
              }}
            >
              {error && <Alert variant="danger" className="mb-2">{error}</Alert>}

              {/* Conversational Question */}
              <div>
                <div style={QUESTION_BLOCK}>
                  <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                    {currentStepData.question}
                  </p>
                </div>

                <Form.Control
                  as="textarea"
                  value={currentStepData.value}
                  onChange={(e) => currentStepData.setter(e.target.value)}
                  placeholder={currentStepData.placeholder}
                  disabled={saving}
                  className="shadow-sm"
                  style={TEXTAREA_STYLE}
                />
              </div>

              {/* Navigation Buttons */}
              <div className="d-flex justify-content-end align-items-center mt-2">
                {!isLastStep ? (
                  <Button onClick={handleNext} disabled={!canProceed || saving}>
                    Continue →
                  </Button>
                ) : (
                  <Button type="submit" disabled={!canProceed || saving} variant="success">
                    {saving ? 'Submitting…' : 'Complete & Submit ✓'}
                  </Button>
                )}
              </div>
            </Form>

            {/* Footer Links */}
            <div style={FOOT_ACTIONS}>
              <Button variant="link" size="sm" onClick={() => navigate('/')}>Home</Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
